// must be loaded before plugins
import FullCalendar from '@fullcalendar/react'

import listPlugin from '@fullcalendar/list'
import { Container, LinearProgress } from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import _ from 'lodash'
import moment from 'moment'
import React from 'react'
import { withRouter } from "react-router-dom"
import { withAppContext } from '../context/AppContextProvider'

const styles = (theme) => ({
    loading: {
        marginTop: theme.spacing(2),
    },
    marginAbove: {
        marginTop: theme.spacing(2),
    },
    container: {
        flexGrow: 1,
        '& .fc': {
            marginTop: theme.spacing(2),
        },
        '& .fc .fc-toolbar-title': {
            fontSize: '1.5em',
        },
        // hack to allow having a border on event dots
        '& .fc .fc-list-event-dot': {
            display: 'inline-block',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            border: 'none',
            boxSizing: 'border-box',
        },
    },
    smallFont: {
        fontSize: '.75em',
    },
    label: {
        padding: '0 2px',
        margin: '0px 2px 0px 1px',
        borderRadius: '3px',
        fontSize: '0.8em',
        textTransform: 'uppercase',
    },
    labelNoShow: {
        backgroundColor: '#000',
        color: '#fff',
    },
    labelRescheduled: {
        backgroundColor: '#6698ff',
    },
    labelReschedule: {
        backgroundColor: '#ff0000',
        color: '#fff',
    },
    labelCheckedIn: {
        backgroundColor: '#7afd57',
        color: '#000',
    },
    labelMissed: {
        backgroundColor: '#f66262',
        color: '#fff',
    },
    labelCompleted: {
        backgroundColor: '#097725',
        color: '#fff',
    },
})

class Agenda extends React.Component {
    calendarRef = React.createRef()

    state = {
        waits: 1,
        employees: [],
        services: [],
        myEmployeeId: 0,
        onlyMine: true,
        eventSources: [],
        initialEventSources: [],
        slotMinTime: null,
        slotMaxTime: null,
        slotDuration: null,
        scrollTime: null,
        businessHours: null,
        exceptions: [],
    }

    componentDidMount = async () => {
        await this.getCalendarData()

        await this.setupEventSources()

        await this.setState({
            waits: this.state.waits - 1,
        })
    }

    getCalendarData = async () => {
        await this.setState({
            waits: this.state.waits + 1,
        })

        try {
            const { settings, employees, services, myEmployeeId } = await this.props.socket.send('agenda', {
                step: 'getCalendarData',
            })

            if (settings.hours) {
                const filledHours = Object.entries(settings.hours).filter(([day, hours]) => hours.open && hours.close)

                if (filledHours.length) {
                    // setup scrollTime setting
                    var earliestHour = filledHours.map(([day, hours]) => hours.open).reduce((a, b) => a < b ? a : b)

                    // subtract 30mins
                    earliestHour = moment('2000-01-01 ' + earliestHour).subtract(moment.duration('00:30')).format('hh:mm')

                    // setup businessHours setting
                    var formattedHours = filledHours.map(([day, hours]) => ({
                        daysOfWeek: [day == '7' ? '0' : day],
                        startTime: hours.open,
                        endTime: hours.close,
                    }))
                }
            }

            if (settings.exceptions) {
                var exceptions = []

                settings.exceptions.forEach((exception) => {
                    // all day exceptions
                    if (!exception.open || !exception.close) {
                        exceptions.push({
                            id: 'exceptions',
                            display: 'background',
                            className: 'fc-non-business',
                            backgroundColor: 'var(--fc-non-business-color, rgba(215, 215, 215, 0.3))',
                            allDay: true,
                            start: exception.date,
                            end: exception.date,
                            resources: [],
                        })
                        // standard exceptions
                    } else {
                        exceptions.push({
                            id: 'exceptions',
                            display: 'background',
                            className: 'fc-non-business',
                            backgroundColor: 'var(--fc-non-business-color, rgba(215, 215, 215, 0.3))',
                            allDay: false,
                            start: exception.date + 'T00:00',
                            end: exception.date + 'T' + exception.open,
                            resources: [],
                        })

                        exceptions.push({
                            id: 'exceptions',
                            display: 'background',
                            className: 'fc-non-business',
                            backgroundColor: 'var(--fc-non-business-color, rgba(215, 215, 215, 0.3))',
                            allDay: false,
                            start: exception.date + 'T' + exception.close,
                            end: exception.date + 'T24:00',
                            resources: [],
                        })
                    }
                })
            }

            this.setState({
                employees,
                services,
                myEmployeeId,
                businessHours: formattedHours,
                exceptions,
                scrollTime: earliestHour,
                slotMinTime: settings.top || '05:00',
                slotMaxTime: settings.bottom || '22:00',
                slotDuration: settings.appt_slot_size || '00:30',
            })

        } catch (err) {
            this.props.showError(err)
        }

        await this.setState({
            waits: this.state.waits - 1,
        })
    }

    setupEventSources = async () => {
        const { employees, onlyMine, myEmployeeId } = this.state

        // one source per-employee
        const eventSources = employees.map(employee => ({
            id: employee.id,
            events: async (info) => (await this.eventFetcher(info))[employee.id],
            backgroundColor: employee.appt_pri_color || 'grey',
            borderColor: employee.appt_pri_color || 'grey',
            failure: () => {
                throw new Error(`Error fetching appointments for ${employee.first_name} ${employee.last_name}`)
            },
        }))

        // one source for notices + appointments by disabled employees
        eventSources.push({
            id: '0',
            events: async (info) => (await this.eventFetcher(info))['0'],
            backgroundColor: 'white',
            borderColor: 'black',
            failure: () => {
                throw new Error('Error fetching notices')
            },
        })

        // one source for staff hours
        eventSources.push({
            id: 'staff-hrs',
            events: async (info) => {
                const start = moment(info.start).format('YYYY-MM-DD')
                const end = moment(info.end).format('YYYY-MM-DD')
                const dates = {}
                const events = []

                const shifts = await this.props.socket.send('agenda', {
                    step: 'getShifts',
                    start,
                    end,
                })

                // per-day
                shifts.forEach((shift) => {
                    const start = moment(shift.date + ' ' + shift.start).format('h:mma')
                    const end = moment(shift.date + ' ' + shift.end).format('h:mma')
                    let time = `${start} - ${end}`
                    let name = `${shift.first_name} ${shift.last_name}`

                    if (shift.start == '00:00:00' && shift.end == '00:00:00') {
                        time = 'All-day'
                    }

                    let title = `${shift.first_name} ${shift.last_name[0]}`

                    const containerEl = document.createElement('DIV')

                    const timeEl = document.createElement('STRONG')
                    timeEl.innerHTML = time

                    const nameEl = document.createElement('SPAN')
                    nameEl.innerHTML = name

                    containerEl.appendChild(timeEl)
                    containerEl.append(' ')
                    containerEl.appendChild(nameEl)

                    if (shift.date in dates === false) {
                        dates[shift.date] = {
                            titles: [],
                            html: [],
                        }
                    }

                    dates[shift.date].titles.push(title)
                    dates[shift.date].html.push(containerEl)
                })

                for (let date in dates) {
                    events.push({
                        id: `${date}-shifts`,
                        start: `${date}T00:00:00`,
                        allDay: true,
                        editable: false,
                        title: dates[date].titles.join(', '),
                        html: dates[date].html,
                    })
                }

                return events
            },
            backgroundColor: 'inherit',
            borderColor: 'transparent',
            failure: () => {
                throw new Error(`Error fetching staff hours`)
            },
        })

        const initialEventSources = eventSources.filter(eventSource => {
            if (onlyMine) {
                return ['0', 'staff-hrs', myEmployeeId].includes(eventSource.id)
            }

            return true
        })

        await this.setState({
            eventSources,
            initialEventSources,
        })
    }

    eventFetcher = async_debounce(async (info) => {
        const { services } = this.state
        const calendar = this.calendarRef.current.getApi()
        const enabledEventSourceIds = calendar.getEventSources().map(eventSource => eventSource.id)

        const ownerIds = this.state.eventSources
            .map(eventSource => eventSource.id)
            .filter(eventSourceId => eventSourceId != 'staff-hrs')
            .filter(eventSourceId => enabledEventSourceIds.includes(eventSourceId))

        let appointmentsById = await this.props.socket.send('agenda', {
            step: 'getAgenda',
            ownerIds,
            start: info.start,
            end: info.end,
            timeZone: info.timeZone,
        })

        appointmentsById = _.mapValues(appointmentsById, appointments => appointments.map(appointment => {
            if (appointment.meeting_url) {
                appointment.url = appointment.meeting_url
            }

            appointment.title = (() => {
                if (appointment.private == '1') {
                    return 'Private'
                }

                let title

                // append attendee names and program abbrevs
                if (appointment.attendees.length) {
                    const names = appointment.attendees
                        .filter(attendee => attendee.type == 'Customer' || attendee.type == 'Lead')
                        .map(attendee => {
                            if (!attendee.name) {
                                attendee.name = `[Missing ${attendee.type}]`
                            } else if (attendee.type == 'Lead') {
                                attendee.name = `${attendee.name} (${attendee.type})`
                            }

                            if (attendee.program_abbrev) {
                                attendee.name = `${attendee.name} (${attendee.program_abbrev})`
                            }

                            return attendee.name
                        })

                    if (names.length) {
                        title = names.join(', ')
                    }
                }

                // append subject
                if (appointment.subject) {
                    title = title ? `${title} • ${appointment.subject}` : appointment.subject
                }

                // append service abbrevs
                if (appointment.resources.length) {
                    const serviceIds = appointment.resources.filter(resource => resource.indexOf('service_') === 0).map(resource => resource.replace('service_', ''))
                    let serviceAbbrevs = services.filter(service => serviceIds.includes(service.id)).map(service => service.abbrev)

                    if (serviceAbbrevs.length) {
                        serviceAbbrevs = serviceAbbrevs.join(', ')
                        title = `${title} • [${serviceAbbrevs}]`
                    }
                }

                return title
            })()

            return appointment
        }))

        return appointmentsById
    }, 100)

    handleEventMount = (info) => {
        if (info.event.display.match(/background/)) { // handles inverse-background too
            return
        }

        const { classes } = this.props
        const dotEl = info.el.getElementsByClassName('fc-list-event-dot')[0]
        const timeEl = info.el.getElementsByClassName('fc-list-event-time')[0]
        const titleEl = info.el.getElementsByClassName('fc-list-event-title')[0]

        // add tooltip
        info.el.setAttribute('title', info.event.title)

        // special cases for employee shift lists
        if (info.event.extendedProps.html) {
            timeEl.innerHTML = 'Staff'

            titleEl.innerHTML = ''

            info.event.extendedProps.html.forEach(html => {
                titleEl.appendChild(html)
            })

            return
        }

        // special cases for notices and unassigned (stream_id = requested employee id)
        if (info.event.extendedProps.stream_id == 0) {
            // employee is disabled
            if (info.event.extendedProps.owner_id != 0) {
                info.el.classList.add('unassigned-appt')
            }

            // ebooking
            if (info.event.extendedProps.owner_id == 0 && info.event.extendedProps.scheduler_id == 0) {
                info.el.classList.add('unassigned-appt')
            }
        }

        if (timeEl && timeEl.innerText == '') {
            timeEl.innerHTML = '<div class="fc-event-time">All Day</div>'
        }

        // add status prefix if needed
        const statuses = {
            NoShow: `<span class="${classes.label} ${classes.labelNoShow}"}>Cancelled</span>`,
            ReScheduled: `<span class="${classes.label} ${classes.labelRescheduled}"}>Rescheduled</span>`,
            Reschedule: `<span class="${classes.label} ${classes.labelReschedule}"}>Reschedule</span>`,
            CheckedIn: `<span class="${classes.label} ${classes.labelCheckedIn}"}>&#10004;</span>`,
            Missed: `<span class="${classes.label} ${classes.labelMissed}"}>Missed</span>`,
            Complete: `<span class="${classes.label} ${classes.labelCompleted}"}>Complete</span>`,
        }

        const status = statuses[info.event.extendedProps.status]

        if (status) {
            titleEl.prepend(`${status} `)
        }

        if (info.event.extendedProps.meeting_url) {
            titleEl.prepend('🎦 ')
        }

        if (dotEl) {
            dotEl.style.backgroundColor = info.event.source.internalEventSource.ui.backgroundColor
        }
    }

    handleViewChange = (info) => {
        const { eventSources, onlyMine: prevOnlyMine, myEmployeeId } = this.state
        const onlyMine = ['listMyDay', 'listMyWeek'].includes(info.view.type)

        // if changing between myX and x
        if (prevOnlyMine != onlyMine) {
            const calendar = this.calendarRef.current.getApi()

            this.setState({
                onlyMine,
            })

            if (onlyMine) {
                // remove sources that aren't mine
                calendar.getEventSources().forEach(eventSource => {
                    const inMyList = ['0', 'staff-hrs', myEmployeeId].includes(eventSource.id)

                    if (!inMyList) {
                        eventSource.remove()
                    }
                })
            } else {
                // add sources that aren't mine
                eventSources.forEach(eventSource => {
                    const inMyList = ['0', 'staff-hrs', myEmployeeId].includes(eventSource.id)

                    if (!inMyList) {
                        calendar.addEventSource(eventSource)
                    }
                })
            }

            this.calendarRef.current.getApi().refetchEvents()
        }
    }

    handleEventClick = (info) => {
        // open links in new tab
        if (info.event.url) {
            info.jsEvent.preventDefault()
            window.open(info.event.url, '_blank')
        }
    }

    render() {
        const { classes } = this.props
        const { waits, initialEventSources, slotMinTime, slotMaxTime, slotDuration, scrollTime, businessHours, exceptions } = this.state

        return (
            <Container className={classes.container}>
                {waits > 0 &&
                    <LinearProgress className={classes.loading} />
                }
                {waits == 0 &&
                    <FullCalendar
                        ref={this.calendarRef}
                        plugins={[listPlugin]}
                        headerToolbar={{
                            start: '',
                            end: 'prev,next listDay,listWeek,listMyDay,listMyWeek',
                        }}
                        views={{
                            listDay: {
                                type: 'list',
                                listDayFormat: { weekday: 'long' },
                                listDaySideFormat: { month: 'long', day: 'numeric', year: 'numeric' },
                                displayEventEnd: false,
                                duration: { days: 1 },
                                buttonText: 'Day',
                            },
                            listWeek: {
                                type: 'list',
                                listDayFormat: { weekday: 'long' },
                                listDaySideFormat: { month: 'long', day: 'numeric', year: 'numeric' },
                                displayEventEnd: false,
                                buttonText: 'Week',
                            },
                            listMyDay: {
                                type: 'list',
                                listDayFormat: { weekday: 'long' },
                                listDaySideFormat: { month: 'long', day: 'numeric', year: 'numeric' },
                                displayEventEnd: false,
                                duration: { days: 1 },
                                buttonText: 'My Day',
                            },
                            listMyWeek: {
                                type: 'list',
                                listDayFormat: { weekday: 'long' },
                                listDaySideFormat: { month: 'long', day: 'numeric', year: 'numeric' },
                                displayEventEnd: false,
                                duration: { weeks: 1 },
                                buttonText: 'My Week',
                            },
                        }}
                        initialView="listMyDay"
                        height="auto"
                        contentHeight="auto"
                        eventTextColor="#000"
                        slotMinTime={slotMinTime}
                        slotMaxTime={slotMaxTime}
                        slotDuration={slotDuration}
                        scrollTime={scrollTime}
                        allDayText=""
                        businessHours={businessHours}
                        initialEvents={exceptions}
                        eventSources={initialEventSources}
                        eventDidMount={this.handleEventMount}
                        eventClick={this.handleEventClick}
                        viewClassNames={this.handleViewChange}
                    />
                }
            </Container>
        )
    }
}

export default withAppContext(withRouter(withStyles(styles, { withTheme: true })(Agenda)))

// FROM https://stackoverflow.com/a/35228455
function async_debounce(inner, ms = 0) {
    let timer = null
    let resolves = []

    return function (...args) {
        // Run the function after a certain amount of time
        clearTimeout(timer)
        timer = setTimeout(() => {
            // Get the result of the inner function, then apply it to the resolve function of
            // each promise that has been created since the last time the inner function was run
            let result = inner.apply(this, args)
            resolves.forEach(r => r(result))
            resolves = []
        }, ms)

        return new Promise(r => resolves.push(r))
    }
}
